import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SEO } from '../components/SEO';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ChevronDownIcon } from 'lucide-react';
const faqs = [{
  question: 'What is KittyPot?',
  answer: 'KittyPot is a shared pot for friends, trips, housemates and nights out. Everyone adds what they spend, KittyPot works out who owes who, and the group settles up together.'
}, {
  question: 'What is a kitty?',
  answer: 'A kitty is one shared pot for a trip, event, house share or night out. It holds the members, the spends and the settle-up for that group.'
}, {
  question: 'When can I get KittyPot?',
  answer: 'KittyPot is in closed testing on iOS and Android right now. Join the waitlist and we will invite you to a testing spot.'
}, {
  question: 'Do my friends need the app to join?',
  answer: 'Friends can open the invite link at join.kittypot.com and join from the browser, where they can add spends and receipts, see who owes who, and settle up.'
}, {
  question: 'How do people join a kitty?',
  answer: 'Share a link, a code or a QR to scan, or invite people straight from your contacts. Admins can approve join requests as people arrive.'
}, {
  question: 'Can I add someone who is not on KittyPot yet?',
  answer: 'Yes. Add them by name so their share is tracked from the start, and they can claim that name later when they join.'
}, {
  question: 'How do I add an expense?',
  answer: 'Add the title and amount, choose who paid, and pick who it is shared with. You can also snap the receipt and let KittyPot fill in the details for you.'
}, {
  question: 'Can I split expenses unequally?',
  answer: 'Yes. Share a spend with only the people involved, then split it equally or set a custom amount for each person.'
}, {
  question: 'Can I log something a friend paid for?',
  answer: 'Yes. Pick any member as the payer, so the kitty always matches who actually paid.'
}, {
  question: 'What is “who owes who”?',
  answer: 'KittyPot works out the fewest payments needed to make everyone even, person to person, and shows the breakdown behind each amount.'
}, {
  question: 'How does settling up work?',
  answer: 'Pay each other by bank transfer or cash, mark the payment as paid, and the person receiving it confirms when it lands. A nudge reminds anyone who still owes you.'
}, {
  question: 'Does KittyPot hold my money?',
  answer: "You're always in control of your money — you settle up directly with each other, your own way."
}, {
  question: 'Can I save my bank details?',
  answer: 'Yes. Save your name, sort code and account number once, then share them with the group when it is time to settle.'
}, {
  question: 'What is My Balance?',
  answer: 'My Balance brings together what you owe and what you are owed across every kitty, with per-person and per-kitty amounts plus your payment history.'
}, {
  question: 'Who manages a kitty?',
  answer: 'Admins rename the kitty, add and remove members, review join requests, and close or delete it when the group is done. Admin roles go to members using the app so a kitty always has someone in charge.'
}, {
  question: 'What happens when a kitty closes?',
  answer: 'The history stays available, every member is still listed, and any outstanding amounts carry into My Balance until each pair has settled.'
}, {
  question: 'Can I leave a kitty?',
  answer: 'Yes. Settle any outstanding amounts first, then leave — in the app or from the web.'
}, {
  question: 'What happens if I lose signal?',
  answer: 'KittyPot keeps working with your last-loaded data, and lets you know when you refresh without a connection.'
}, {
  question: 'Is there anything fun in there?',
  answer: "Yes — spin the wheel for \u201cWho\u2019s round is next?\u201d and let the group decide it the entertaining way."
}];
export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <div className="min-h-screen bg-[#FAFAFA] font-sans selection:bg-kitty-yellow-200 selection:text-kitty-dark" data-id="element-508" data-mp="2e1cahh8seo5u">
      <SEO title="FAQ | KittyPot — Group Money Made Simple" description="Answers about KittyPot: creating a shared pot, joining from a link, adding expenses and receipts, seeing who owes who, and settling up together." ogTitle="KittyPot FAQ" ogDescription="Create shared pots, track expenses, settle up together." path="/faq" jsonLd={{
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map(faq => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer
        }
      }))
    }} data-id="element-509" data-mp="2a124w5owpzri" />
      <Navbar data-id="element-510" data-mp="1hc4mm2kp42yr" />
      <main className="pt-36 pb-24" data-id="element-511" data-mp="1cbgzhrzl869t">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-512" data-mp="1ccia5vcoyqr0">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="text-center mb-14" data-id="element-513" data-mp="3nysrwmsvlq8l">
            <h1 className="text-4xl md:text-5xl font-black text-kitty-dark mb-4 tracking-tight" data-id="element-514" data-mp="1sdtalgx08up">
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-gray-500 max-w-lg mx-auto" data-id="element-515" data-mp="5ql9nf7gv85d">
              Everything you need to know about KittyPot. Can't find what you're
              looking for?{' '}
              <a href="/contact" className="text-kitty-yellow-600 hover:text-kitty-yellow-700 font-medium underline underline-offset-2" data-id="element-516" data-mp="3a7e2ny8dkst3">
                Get in touch
              </a>
              .
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="space-y-3" data-id="element-517" data-mp="2mhti5awkpnhp">
            {faqs.map((faq, index) => <div key={index} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-id="element-518" data-mp="3trzk2624r74s" data-mpi={index}>
                <button onClick={() => toggle(index)} className="w-full flex items-center justify-between px-6 py-5 text-left gap-4" data-id="element-519" data-mp="3ot519445xiko">
                  <span className="text-base font-semibold text-kitty-dark leading-snug" data-id="element-520" data-mp="2f4vfxiy7ffk">
                    {faq.question}
                  </span>
                  <motion.div animate={{
                rotate: openIndex === index ? 180 : 0
              }} transition={{
                duration: 0.2
              }} className="flex-shrink-0" data-id="element-521" data-mp="3axauaqmy93a7">
                    <ChevronDownIcon className="w-5 h-5 text-gray-400" data-id="element-522" data-mp="3jts6lx71v77j" />
                  </motion.div>
                </button>
                <AnimatePresence initial={false} data-id="element-523" data-mp="z5dx3offx182">
                  {openIndex === index && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.25,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-524" data-mp="z7i9kuren8jy">
                      <div className="px-6 pb-5 pt-0" data-id="element-525" data-mp="srrrl8l0ke95">
                        <p className="text-gray-500 leading-relaxed text-[15px]" data-id="element-526" data-mp="xp9vulo365at">
                          {faq.answer}
                        </p>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </motion.div>

          {/* Bottom CTA */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="text-center mt-16 bg-gray-950 rounded-2xl p-10" data-id="element-527" data-mp="1bnq9yxoa2y2z">
            <h2 className="text-2xl font-bold text-white mb-3" data-id="element-528" data-mp="293ngzirgg6n5">
              Still have questions?
            </h2>
            <p className="text-gray-400 mb-6 max-w-md mx-auto" data-id="element-529" data-mp="91xojaq9cqhp">
              We're happy to help. Reach out and we'll get back to you within 24
              hours.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-530" data-mp="93ug765jmjs">
              <a href="/contact" className="bg-kitty-yellow hover:bg-kitty-yellow-500 text-kitty-dark px-8 py-3 rounded-full font-bold transition-all active:scale-95 shadow-md hover:shadow-lg" data-id="element-531" data-mp="3rfdl4fdmjzkb">
                Contact Us
              </a>
              <a href="mailto:support@kittypot.com" className="text-gray-400 hover:text-white font-medium transition-colors text-sm" data-id="element-532" data-mp="nzq0jswliwr5">
                or email support@kittypot.com
              </a>
            </div>
          </motion.div>
        </div>
      </main>
      <Footer data-id="element-533" data-mp="33pt25whh12b1" />
    </div>;
}